// Session-level environment capture for mic telemetry (schema v2).
// Capability flags are the primary diagnostic signal; UA parsing is a display
// label only. Captured once per page load, sent with the session's first log.

let cachedContext = null;
let sessionId = null;

export const getSessionId = () => {
  if (!sessionId) {
    sessionId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
  return sessionId;
};

export const getCachedContext = () => cachedContext;

// Most-specific token first: several browsers embed another browser's token.
export const parseBrowserFallback = (ua) => {
  const rules = [
    ["Edge", /Edg(?:e|A|iOS)?\/(\d+)/],
    ["Opera", /OPR\/(\d+)/],
    ["Samsung Internet", /SamsungBrowser\/(\d+)/],
    ["Chrome iOS", /CriOS\/(\d+)/],
    ["Firefox iOS", /FxiOS\/(\d+)/],
    ["Firefox", /Firefox\/(\d+)/],
    ["Chrome", /Chrome\/(\d+)/],
    ["Safari", /Version\/(\d+(?:\.\d+)?).*Safari/],
  ];
  for (const [name, re] of rules) {
    const m = ua.match(re);
    if (m) return { name, version: m[1] };
  }
  return { name: "Unknown", version: null };
};

export const detectOS = () => {
  const ua = navigator.userAgent;
  // iPadOS masquerades as macOS since iOS 13 — touch points disambiguate
  if (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    return "iPadOS";
  if (/iPhone|iPad|iPod/.test(ua)) return "iOS";
  if (/Android/.test(ua)) return "Android";
  if (/Windows/.test(ua)) return "Windows";
  if (/Macintosh/.test(ua)) return "macOS";
  if (/CrOS/.test(ua)) return "ChromeOS";
  if (/Linux/.test(ua)) return "Linux";
  return "Unknown";
};

export const captureSessionContext = async () => {
  if (cachedContext) return cachedContext;

  const capabilities = {
    audiocontext_ctor: window.AudioContext
      ? "AudioContext"
      : window.webkitAudioContext
      ? "webkitAudioContext"
      : "none",
    has_audio_worklet: typeof AudioWorkletNode !== "undefined",
    has_float_time_domain:
      typeof AnalyserNode !== "undefined" &&
      !!AnalyserNode.prototype.getFloatTimeDomainData,
    has_permissions_api: !!(navigator.permissions && navigator.permissions.query),
    has_enumerate_devices: !!(
      navigator.mediaDevices && navigator.mediaDevices.enumerateDevices
    ),
    has_get_user_media: !!(
      navigator.mediaDevices && navigator.mediaDevices.getUserMedia
    ),
    has_ua_client_hints: !!navigator.userAgentData,
    has_send_beacon: !!navigator.sendBeacon,
  };

  let uaCh = null;
  if (navigator.userAgentData) {
    uaCh = {
      brands: navigator.userAgentData.brands,
      platform: navigator.userAgentData.platform,
      mobile: navigator.userAgentData.mobile,
    };
    try {
      const he = await navigator.userAgentData.getHighEntropyValues([
        "platformVersion",
        "fullVersionList",
      ]);
      uaCh.platform_version = he.platformVersion;
      uaCh.full_version_list = he.fullVersionList;
    } catch (_) {
      // high-entropy hints may be denied; low-entropy is enough
    }
  }

  cachedContext = {
    session_id: getSessionId(),
    schema_version: 2,
    captured_at: new Date().toISOString(),
    capabilities,
    ua_ch: uaCh,
    browser: parseBrowserFallback(navigator.userAgent),
    os: detectOS(),
    engine: uaCh
      ? "blink"
      : /Firefox\//.test(navigator.userAgent)
      ? "gecko"
      : /AppleWebKit/.test(navigator.userAgent)
      ? "webkit"
      : "unknown",
    ua_raw: navigator.userAgent,
    viewport: {
      w: window.innerWidth,
      h: window.innerHeight,
      dpr: window.devicePixelRatio,
    },
    language: navigator.language,
  };
  return cachedContext;
};
